
import Vue from 'vue';
import {
  computed, defineComponent, ref, reactive, watch,
} from '@vue/composition-api';
import Donut from 'vue-css-donut-chart';
import 'vue-css-donut-chart/dist/vcdonut.css';
import { mapMutations } from 'vuex';
import store from '@/store';
import djangoRest from '@/django';
import { Project, ScanState } from '@/types';
import ExperimentsView from '@/components/ExperimentsView.vue';
import Navbar from '@/components/Navbar.vue';
import ProjectSettings from '@/components/ProjectSettings.vue';
import ProjectUsers from '@/components/ProjectUsers.vue';

Vue.use(Donut);

export default defineComponent({
  name: 'Projects',
  components: {
    ExperimentsView,
    Navbar,
    ProjectSettings,
    ProjectUsers,
  },
  inject: ['user', 'MIQAConfig'],
  setup() {
    const { switchReviewMode } = store.commit;
    const loadingProjects = ref(true);
    store.dispatch.loadProjects().then(() => {
      loadingProjects.value = false;
    });
    const reviewMode = computed(() => store.state.reviewMode);
    const complete = window.location.hash.includes('complete');
    const currentProject = computed(() => store.state.currentProject);
    const currentTaskOverview = computed(() => store.state.currentTaskOverview);
    const projects = computed(() => store.state.projects);
    const isGlobal = computed(() => store.getters.isGlobal);
    const selectedProjectIndex = ref(projects.value.findIndex(
      (project) => project.id === currentProject.value?.id,
    ));
    const selectGlobal = () => {
      store.dispatch.loadGlobal();
    };

    const overviewSections = ref([]);
    const scanStates = Object.keys(ScanState);
    const setOverviewSections = () => {
      if (projects.value && currentTaskOverview.value) {
        const scanStateCounts = ref(reactive(
          scanStates.map(
            (stateString) => {
              const stateCount = Object.entries(currentTaskOverview.value.scan_states).filter(
                ([, scanState]) => scanState === stateString.replace(/_/g, ' '),
              ).length;
              return [stateString, stateCount];
            },
          ),
        ));
        overviewSections.value = scanStateCounts.value.map(
          ([stateString, scanCount]: [string, number]) => ({
            value: scanCount,
            label: `${stateString.replace(/_/g, ' ')} (${scanCount})`,
            color: ScanState[stateString],
          }),
        );
      } else {
        overviewSections.value = [];
      }
    };

    async function refreshTaskOverview() {
      if (currentProject.value) {
        const taskOverview = await djangoRest.projectTaskOverview(currentProject.value.id);
        if (JSON.stringify(store.state.currentTaskOverview) !== JSON.stringify(taskOverview)) {
          store.commit.setTaskOverview(taskOverview);
        }
      }
    }

    async function refreshAllTaskOverviews() {
      projects.value.forEach(
        async (project: Project) => {
          const taskOverview = await djangoRest.projectTaskOverview(project.id);
          store.commit.setTaskOverview(taskOverview);
        },
      );
    }

    async function getProjectFromURL() {
      if (complete) {
        const targetProjectIndex = projects.value.findIndex(
          (project) => project.id === window.location.hash.split('/')[1],
        );
        const targetProject = projects.value[targetProjectIndex];
        if (targetProject) store.commit.setCurrentProject(targetProject);
        selectedProjectIndex.value = targetProjectIndex;
      }
    }

    const overviewPoll = setInterval(refreshTaskOverview, 10000);
    watch(currentTaskOverview, setOverviewSections);
    watch(currentProject, refreshTaskOverview);
    watch(projects, getProjectFromURL);

    return {
      reviewMode,
      switchReviewMode,
      complete,
      currentProject,
      loadingProjects,
      currentTaskOverview,
      selectedProjectIndex,
      projects,
      isGlobal,
      overviewPoll,
      selectGlobal,
      overviewSections,
      setOverviewSections,
      refreshAllTaskOverviews,
      getProjectFromURL,
    };
  },
  data: () => ({
    creating: false,
    newName: '',
  }),
  watch: {
    projects() {
      this.$nextTick(() => {
        if (this.$refs.proceed) this.$refs.proceed.$el.focus();
      });
    },
  },
  mounted() {
    this.setOverviewSections();
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this.createProject();
      }
    });
    window.addEventListener('unauthorized', () => {
      this.$snackbar({
        text: 'Server session expired. Try again.',
        timeout: 6000,
      });
    });
  },
  beforeDestroy() {
    clearInterval(this.overviewPoll);
  },
  methods: {
    ...mapMutations(['setProjects', 'setCurrentProject']),
    selectProject(project: Project) {
      if (this.complete) {
        this.complete = false;
      }
      store.dispatch.loadProject(project);
    },
    async createProject() {
      if (this.creating && this.newName.length > 0) {
        try {
          const newProject = await djangoRest.createProject(this.newName);
          this.setProjects(this.projects.concat([newProject]));
          store.dispatch.loadProject(newProject);
          this.creating = false;
          this.newName = '';

          this.$snackbar({
            text: 'New project created.',
            timeout: 6000,
          });
        } catch (ex) {
          this.$snackbar({
            text: ex || 'Project creation failed.',
          });
        }
      }
    },
    async proceedToNext() {
      const nextProject = this.projects[this.selectedProjectIndex + 1];
      store.dispatch.loadProject(nextProject);
      this.selectedProjectIndex += 1;
      await djangoRest.projectTaskOverview(nextProject.id).then(
        (taskOverview) => {
          let nextScanIndex = 0;
          let nextScan;
          let nextScanState;
          while (
            !nextScan || (nextScanState === 'complete' && this.reviewMode)
          ) {
            nextScan = nextProject.experiments[0].scans[nextScanIndex];
            nextScanState = taskOverview.scan_states[nextScan.id];
            nextScanIndex += 1;
          }
          this.$router.push(`/${nextProject.id}/${nextScan.id}` || '');
        },
      );
    },
  },
});
